import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft, Share2, Calendar, MapPin, Monitor, Award,
    Clock, CheckCircle, Info, X, Mail, Globe, Copy, Briefcase, Zap, Shield, PlayCircle, BookOpen, Users, GraduationCap, TrendingUp
} from 'lucide-react';
import { showSuccess, showError, showLoading, closeLoading, showConfirm } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import { API_URL } from '../../../config';
import './CourseDetail.css';

const CourseDetail = ({ courseId: propCourseId, onClose }) => {
    const { courseId: paramCourseId } = useParams();
    const courseId = propCourseId || paramCourseId;
    const navigate = useNavigate();

    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('overview');
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    const tabsRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const isModal = Boolean(onClose);

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
        document.body.style.overflow = 'hidden';
        fetchDetails();
        return () => {
            document.body.style.overflow = '';
        };
    }, [courseId]);

    const fetchDetails = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/student/course-details/${courseId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDetails(res.data);
            setLoading(false);
        } catch (err) {
            showError("Load Error", "Could not retrieve course details.");
            setLoading(false);
        }
    };

    const loadRazorpay = () => new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });

    const handleEnroll = async () => {
        const { course } = details;

        if (!selectedBatchId) {
            showError('Batch Selection Required', 'Please select a batch from the Batches tab before enrolling.');
            setActiveSection('batches');
            if (tabsRef.current) tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }

        const confirmResult = await showConfirm(
            'Confirm Enrollment',
            `Enroll in ${course.title}\nInvestment: $${course.price?.toLocaleString()}`
        );
        if (!confirmResult.isConfirmed) return;

        showLoading('Initializing Secure Payment Gateway...');
        const loaded = await loadRazorpay();
        if (!loaded) {
            closeLoading();
            showError('Payment Error', 'Razorpay SDK failed to load.');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const orderRes = await axios.post('/api/student/razorpay/order', { courseId: course._id }, { headers: { Authorization: `Bearer ${token}` } });
            closeLoading();

            const options = {
                key: orderRes.data.key,
                amount: orderRes.data.amount,
                currency: "USD",
                name: "ZenX EduTech",
                description: course.title,
                order_id: orderRes.data.id,
                prefill: {},
                handler: async (response) => {
                    showLoading('Verifying Transaction...');
                    try {
                        await axios.post('/api/student/razorpay/verify', {
                            ...response,
                            courseId: course._id,
                            batchId: selectedBatchId
                        }, { headers: { Authorization: `Bearer ${token}` } });

                        await axios.post('/api/student/invoices/create', {
                            courseId: course._id,
                            paymentId: response.razorpay_payment_id,
                            amount: course.price
                        }, { headers: { Authorization: `Bearer ${token}` } });

                        closeLoading();
                        showSuccess('Enrollment Confirmed!', `You are now enrolled in "${course.title}". Job Portal access is now ACTIVE.`);

                        if (isModal) onClose();
                        setTimeout(() => navigate('/student-dashboard/courses'), 2000);
                    } catch (err) {
                        closeLoading();
                        showError('Verification Issue', err.response?.data?.message || 'Payment confirmed but verification failed.');
                    }
                },
                theme: { color: '#1e88e5' }
            };
            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', (res) => showError('Payment Failed', res.error.description));
            rzp.open();
        } catch (err) {
            closeLoading();
            showError('Gateway Error', 'Failed to initiate secure order. Please try again.');
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else if (window.opener || window.history.length <= 2) {
            window.close();
        } else {
            navigate(-1);
        }
    };

    const handleLinkedIn = () => {
        if (details?.course?.tutorId?.linkedIn) {
            window.open(details.course.tutorId.linkedIn, '_blank');
        } else {
            showError('Profile Missing', 'LinkedIn profile is not provided by the tutor yet.');
        }
    };

    const handlePortfolio = () => {
        if (details?.course?.tutorId?.portfolio) {
            window.open(details.course.tutorId.portfolio, '_blank');
        } else {
            showError('Portfolio Missing', 'Tutor portfolio is not available yet.');
        }
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        showSuccess('Link Copied!', 'Course link copied to clipboard.');
    };

    const handleGetSyllabus = () => {
        setActiveSection('curriculum');
        if (tabsRef.current) {
            tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    if (loading) return <Loader />;
    if (!details) return null;

    const { course, syllabus, isEnrolled } = details;

    const sections = [
        { id: 'overview', label: 'Overview' },
        { id: 'curriculum', label: 'Curriculum' },
        { id: 'instructor', label: 'Instructor Profile' },
        { id: 'batches', label: 'Batches' }
    ];

    const courseDetailContent = (
        <div className="course-detail-root-v2">

            {/* Share Modal Portal Layer */}
            <AnimatePresence>
                {isShareModalOpen && (
                    <div className="share-modal-overlay" onClick={() => setIsShareModalOpen(false)}>
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="share-modal-content"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="sm-header">
                                <h3>Share this Course</h3>
                                <button className="close-sm" onClick={() => setIsShareModalOpen(false)}><X size={20} /></button>
                            </div>
                            <div className="sm-body">
                                <button className="share-opt" onClick={() => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(course.title + " " + window.location.href)}`, '_blank')}>
                                    <Globe size={50} color="#25D366" />
                                    <span>WhatsApp</span>
                                </button>
                                <button className="share-opt" onClick={() => window.location.href = `mailto:?subject=${encodeURIComponent(course.title)}&body=${encodeURIComponent(window.location.href)}`}>
                                    <Mail size={50} color="#3b82f6" />
                                    <span>Email</span>
                                </button>
                                <button className="share-opt" onClick={handleCopyLink}>
                                    <Copy size={50} color="#64748b" />
                                    <span>Copy Link</span>
                                </button>
                            </div>
                            <div className="sm-footer">
                                <input type="text" readOnly value={window.location.href} />
                                <button onClick={handleCopyLink}>Copy</button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Floating Nav matching the logic requirement */}
            <div className="cd-floating-nav">
                <div className="cd-nav-left">
                    <button className="cd-nav-btn" onClick={handleClose}>
                        <ArrowLeft size={18} /> <span>Back</span>
                    </button>
                </div>
                <div className="cd-nav-right">
                    <button className="cd-nav-btn" onClick={() => setIsShareModalOpen(true)}>
                        <Share2 size={18} /> <span>Share</span>
                    </button>
                    {isEnrolled ? (
                        <button className="cd-nav-btn cd-enroll-btn enrolled" onClick={() => navigate(`/student-dashboard/learning/${course._id}`)}>
                            Go to Course
                        </button>
                    ) : (
                        <button className="cd-nav-btn cd-enroll-btn" onClick={handleEnroll}>
                            Enroll Now
                        </button>
                    )}
                </div>
            </div>

            <main className="cd-main-wrapper">
                {/* Hero matches screenshot dark block precisely */}
                <div className="cd-hero-block">
                    <div className="cd-hero-decoration">
                        <div className="cd-hero-sparkle">✦</div>
                        <div className="cd-hero-sparkle s2">✦</div>
                    </div>

                    <div className="cd-hero-content">
                        <h1 className="cd-title">{course.title}</h1>
                        <div className="cd-instructor-badge">
                            {course.tutorId?.profileImage ? (
                                <img src={`${API_URL}${course.tutorId.profileImage}`} alt={course.tutorId?.name} />
                            ) : (
                                <div className="cd-avatar-placeholder">{course.tutorId?.name?.charAt(0) || 'T'}</div>
                            )}
                            <div className="cd-instructor-info">
                                <h4>{course.tutorId?.name || 'Expert Instructor'}</h4>
                                <p>{course.tutorId?.headline || 'Professional Tutor'}</p>
                            </div>
                        </div>
                        <div className="cd-tags">
                            <span className="cd-tag">{course.category}</span>
                            <span className="cd-tag">{course.difficultyLevel}</span>
                            <span className="cd-tag">{course.courseMode}</span>
                        </div>
                    </div>
                </div>

                <div className="cd-layout">
                    <div className="cd-left-column">
                        <div className="cd-tabs" ref={tabsRef}>
                            {sections.map(s => (
                                <div
                                    key={s.id}
                                    className={`cd-tab ${activeSection === s.id ? 'active' : ''}`}
                                    onClick={() => setActiveSection(s.id)}
                                >
                                    {s.label}
                                </div>
                            ))}
                        </div>

                        <div className="cd-tab-content-area">
                            {activeSection === 'overview' && (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="cd-overview-area">
                                    <div className="cd-tab-content-card">
                                        <h2>Overview</h2>

                                        <h3>About This Course</h3>
                                        <p>{course.description}</p>

                                        <h3>Prerequisites</h3>
                                        {course.prerequisites?.length > 0 ? (
                                            <ul className="cd-prereq-list">
                                                {course.prerequisites.map((p, i) => <li key={i}>{p}</li>)}
                                            </ul>
                                        ) : (
                                            <p className="text-muted" style={{ color: '#9ca3af', fontStyle: 'italic' }}>No specific prerequisites required. Open to all learners.</p>
                                        )}
                                    </div>

                                    {/* Full Width section matching your request */}
                                    <div className="cd-full-width-section">
                                        <h2>Why Choose This Course?</h2>
                                        <div className="cd-benefits-grid">
                                            <div className="cd-benefit">
                                                <Briefcase size={28} color="#1e88e5" />
                                                <h4>Job Portal Access</h4>
                                                <p>Get exclusive access to premium job opportunities upon completion.</p>
                                            </div>
                                            <div className="cd-benefit">
                                                <Zap size={28} color="#1e88e5" />
                                                <h4>Live Projects</h4>
                                                <p>Work on real-world industry projects to build your portfolio.</p>
                                            </div>
                                            <div className="cd-benefit">
                                                <Shield size={28} color="#1e88e5" />
                                                <h4>Lifetime Access</h4>
                                                <p>Access all course materials and updates forever.</p>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {activeSection === 'curriculum' && (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="cd-tab-content-card">
                                    <h2>Curriculum</h2>
                                    {course.modules?.length > 0 ? (
                                        <div className="cd-accordion-list">
                                            {course.modules.map((module, mIdx) => {
                                                const moduleLessons = syllabus.filter(l => l.moduleId === module._id);
                                                return (
                                                    <div key={module._id} className="cd-accordion-item">
                                                        <div className="cd-acc-header">
                                                            <h4>{mIdx + 1}. {module.moduleTitle}</h4>
                                                            <span className="cd-acc-count">{moduleLessons.length} classes</span>
                                                        </div>
                                                        <div className="cd-acc-body">
                                                            {module.moduleDescription && <p className="cd-module-desc">{module.moduleDescription}</p>}
                                                            {moduleLessons.map((lesson, idx) => (
                                                                <div key={idx} className="cd-lesson-item">
                                                                    <div className="cd-lesson-title">
                                                                        {lesson.contentType === 'Video' ? <PlayCircle size={16} /> : <BookOpen size={16} />}
                                                                        {lesson.title}
                                                                    </div>
                                                                    {lesson.duration && <span className="cd-lesson-meta">{lesson.duration}m</span>}
                                                                </div>
                                                            ))}
                                                            {moduleLessons.length === 0 && <p className="cd-lesson-meta" style={{ margin: 0 }}>No lessons published yet.</p>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="empty-state">
                                            <BookOpen size={48} />
                                            <p>Curriculum is being updated. Please check back later.</p>
                                        </div>
                                    )}
                                </motion.div>
                            )}

                            {activeSection === 'instructor' && (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="cd-tab-content-card">
                                    <h2>Instructor Profile</h2>
                                    <div className="cd-instructor-view">
                                        {course.tutorId?.profileImage ? (
                                            <img src={`${API_URL}${course.tutorId.profileImage}`} alt="Tutor" />
                                        ) : (
                                            <div className="cd-avatar-placeholder" style={{ width: 120, height: 120, fontSize: '3rem' }}>
                                                {course.tutorId?.name?.charAt(0) || 'T'}
                                            </div>
                                        )}
                                        <div className="cd-inst-content">
                                            <h3>{course.tutorId?.name || 'Expert Instructor'}</h3>
                                            <p>{course.tutorId?.bio || 'Dedicated industry professional with practical experience.'}</p>

                                            <div className="cd-inst-actions">
                                                <button className="btn-outline" onClick={handlePortfolio}>View Portfolio</button>
                                                <button className="btn-outline primary" onClick={handleLinkedIn}>Follow on LinkedIn</button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {activeSection === 'batches' && (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="cd-tab-content-card">
                                    <h2>Available Batches</h2>
                                    <div className="batches-grid">
                                        {course.batches?.length > 0 ? course.batches.map((batch) => {
                                            const enrolledCount = batch.enrolledStudents || 0;
                                            const isFull = enrolledCount >= batch.maxStudents;
                                            const isSelected = selectedBatchId === batch._id;

                                            return (
                                                <div key={batch._id} className={`batch-card ${isSelected ? 'selected' : ''} ${isFull ? 'full' : ''}`}>
                                                    <div className="bc-header">
                                                        <h4><Users size={18} /> {batch.batchName}</h4>
                                                        <span className={`status-badge ${isFull ? 'full' : ''}`}>{isFull ? 'Batch Full' : 'Available'}</span>
                                                    </div>
                                                    <div className="bc-info-row">
                                                        <div><Calendar size={18} /><div><label>Starts On</label><span>{new Date(batch.startDate).toLocaleDateString()}</span></div></div>
                                                        <div><TrendingUp size={18} /><div><label>Seats</label><span>{enrolledCount} / {batch.maxStudents}</span></div></div>
                                                    </div>
                                                    <div className="bc-footer">
                                                        <button
                                                            className={`btn-batch ${isSelected ? 'active' : ''}`}
                                                            onClick={() => !isFull && setSelectedBatchId(batch._id)}
                                                            disabled={isFull}
                                                        >
                                                            {isFull ? 'Fully Booked' : isSelected ? 'Selected' : 'Select This Batch'}
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                        }) : (
                                            <div className="empty-state" style={{ gridColumn: '1/-1' }}>
                                                <Calendar size={48} />
                                                <p>New batches are being scheduled. Check back soon.</p>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </div>

                    <div className="cd-right-column">
                        <div className="cd-price-card">
                            <div className="cd-video-preview">
                                {course.promotionalVideoUrl ? (
                                    <video controls poster={course.thumbnailUrl ? `${API_URL}${course.thumbnailUrl}` : undefined}>
                                        <source src={`${API_URL}${course.promotionalVideoUrl}`} />
                                    </video>
                                ) : (
                                    <img src={`${API_URL}${course.thumbnailUrl}`} alt={course.title} />
                                )}
                            </div>

                            <div className="cd-price-amount">
                                ${course.price?.toLocaleString()}
                            </div>
                            <div className="cd-deadline">
                                Registration opens year-round
                            </div>

                            {!isEnrolled && (
                                <button className="cd-btn-primary" onClick={handleGetSyllabus}>
                                    Get Syllabus
                                </button>
                            )}

                            <div className="cd-training-summary">
                                <h4>Training summary</h4>
                                <div className="cd-ts-item">
                                    <Clock className="cd-ts-icon" size={18} />
                                    <span>{course.duration || 'Flexible Duration'}</span>
                                </div>
                                <div className="cd-ts-item">
                                    <Calendar className="cd-ts-icon" size={18} />
                                    <span>Paced Sessions / Live Classes</span>
                                </div>
                                <div className="cd-ts-item">
                                    <MapPin className="cd-ts-icon" size={18} />
                                    <span>Online Format</span>
                                </div>
                                <div className="cd-ts-item">
                                    <Award className="cd-ts-icon" size={18} />
                                    <span>Certificate on Completion</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );

    return createPortal(
        <div className="course-detail-portal-wrapper" ref={scrollContainerRef}>
            {courseDetailContent}
        </div>,
        document.body
    );
};

export default CourseDetail;